/**
 * Audit log viewer (`/admin/audit`) — docs/08-frontend-and-ux.md §3.11, docs/05 §13.1.
 *
 * "Read-only by construction": the api offers no write verb on `audit_log` at
 * all, so this screen is not a permission away from being an editor — there is
 * nothing to edit. Filters are actor / action namespace / entity type / date
 * range, and a row expands to its `detail` jsonb.
 *
 * `entity_type` is the **table name**, plural (ARCHITECTURE.md §2.1 C14); the
 * options come from `AUDIT_ENTITY_TYPE` in `packages/shared` so this list cannot
 * drift from the one the api filters on.
 */

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AUDIT_ENTITY_TYPE, type AuditEntry } from '@famtree/shared';
import { auditApi } from '../../api/client.js';
import { problemMessage } from '../../ui/problem.js';
import ui from '../../ui/components.module.css';
import styles from './audit.module.css';

/** The `namespace.*` filters worth one click, in the order docs/07 §10 lists them. */
const ACTION_NAMESPACES = [
  'proposal',
  'vote',
  'seat',
  'person',
  'relation',
  'settings',
  'bootstrap',
  'user',
  'session',
] as const;

export function AuditLogPage(): JSX.Element {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();

  const action = params.get('action') ?? '';
  const entityType = params.get('entity_type') ?? '';
  const entityId = params.get('entity_id') ?? '';
  const actor = params.get('actor_user_id') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  const setParam = (key: string, value: string): void => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const entries = useQuery({
    queryKey: ['audit', { action, entityType, entityId, actor, from, to }],
    queryFn: () =>
      auditApi.list({
        ...(action ? { action } : {}),
        ...(entityType ? { entity_type: entityType } : {}),
        ...(entityId ? { entity_id: entityId } : {}),
        ...(actor ? { actor_user_id: actor } : {}),
        // The api wants RFC 3339 bounds; a date input gives a bare day.
        ...(from ? { from: `${from}T00:00:00Z` } : {}),
        ...(to ? { to: `${to}T23:59:59Z` } : {}),
        limit: 100,
      }),
    placeholderData: keepPreviousData,
  });

  return (
    <div>
      <div className={styles.header}>
        <h1>{t('audit.title')}</h1>
        <span className={styles.spacer} />
        <Link className={ui.buttonSecondary} to="/council">
          {t('nav.council')}
        </Link>
      </div>
      <p className={ui.muted}>{t('audit.explain')}</p>

      <div className={styles.filters}>
        <label className={ui.field}>
          <span className={ui.label}>{t('audit.action')}</span>
          <select
            className={ui.input}
            value={action}
            onChange={(event) => setParam('action', event.target.value)}
          >
            <option value="">{t('audit.allActions')}</option>
            {ACTION_NAMESPACES.map((namespace) => (
              <option key={namespace} value={`${namespace}.*`}>
                {namespace}.*
              </option>
            ))}
          </select>
        </label>

        <label className={ui.field}>
          <span className={ui.label}>{t('audit.entityType')}</span>
          <select
            className={ui.input}
            value={entityType}
            onChange={(event) => setParam('entity_type', event.target.value)}
          >
            <option value="">{t('audit.allEntities')}</option>
            {AUDIT_ENTITY_TYPE.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>

        <label className={ui.field}>
          <span className={ui.label}>{t('audit.actor')}</span>
          <select
            className={ui.input}
            value={actor}
            onChange={(event) => setParam('actor_user_id', event.target.value)}
          >
            <option value="">{t('audit.anyActor')}</option>
            {/* The literal `null` is the system-job filter (docs/05 §13.1). */}
            <option value="null">{t('audit.systemOnly')}</option>
          </select>
        </label>

        <label className={ui.field}>
          <span className={ui.label}>{t('audit.from')}</span>
          <input
            className={ui.input}
            type="date"
            value={from}
            onChange={(event) => setParam('from', event.target.value)}
          />
        </label>

        <label className={ui.field}>
          <span className={ui.label}>{t('audit.to')}</span>
          <input
            className={ui.input}
            type="date"
            value={to}
            onChange={(event) => setParam('to', event.target.value)}
          />
        </label>
      </div>

      {entries.isPending && <p className={ui.muted}>{t('app.loading')}</p>}
      {entries.isError && <p className={ui.problem}>{problemMessage(entries.error, t)}</p>}

      {entries.data && entries.data.data.length === 0 && (
        <p className={ui.muted}>{t('audit.empty')}</p>
      )}

      {entries.data && entries.data.data.length > 0 && (
        <>
          <p className={ui.muted}>{t('audit.count', { count: entries.data.page.total ?? 0 })}</p>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">{t('audit.when')}</th>
                <th scope="col">{t('audit.actor')}</th>
                <th scope="col">{t('audit.action')}</th>
                <th scope="col">{t('audit.entity')}</th>
              </tr>
            </thead>
            <tbody>
              {entries.data.data.map((entry) => (
                <Row key={entry.id} entry={entry} />
              ))}
            </tbody>
          </table>
          {entries.data.page.has_more && <p className={ui.muted}>{t('audit.moreAvailable')}</p>}
        </>
      )}
    </div>
  );
}

function Row({ entry }: { entry: AuditEntry }): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const hasDetail = Object.keys(entry.detail).length > 0;

  return (
    <>
      <tr className={styles.row} onClick={() => hasDetail && setOpen((value) => !value)}>
        <td className="tabular">{formatWhen(entry.at)}</td>
        <td>{entry.actor_display_name ?? <span className={ui.muted}>{t('audit.system')}</span>}</td>
        <td>
          <code>{entry.action}</code>
        </td>
        <td className={ui.muted}>
          {entry.entity_type}
          {hasDetail && (
            <button className={styles.expand} type="button" aria-expanded={open}>
              {open ? t('audit.hideDetail') : t('audit.showDetail')}
            </button>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={4}>
            <pre className={styles.detail}>{JSON.stringify(entry.detail, null, 2)}</pre>
          </td>
        </tr>
      )}
    </>
  );
}

function formatWhen(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
